import React, { useEffect, useMemo } from "react";
import useViewport from "components/general/useViewPort";
import { getRscObject } from "helpers/rsc";
import { useQuery, useQueries } from "react-query";
import { getCumulativeResult, getResultsRSC } from "data/Querys";
import NotAvailable from "components/common/NotAvailable";
import Loader from "components/general/Loader";
import MirsTable from "components/general/table/Table";
import { ColumnNoc, ColumnName, ColumnRank, ColumnQualification, ColumnExpander, ColumnBib } from "components/common/TableColumns";
import Label from "components/general/Label";
import LoaderSubComponent from "components/general/LoaderSubComponent";
import { TableExpandedForHeats as TableExpanded } from "components/sports/csl/events/canoe/TableExpanded";
import { Currents } from "components/sports/csl/events/canoe/Currents";
import PageHeader from "components/common/PageHeader";
import ProgressionRule from "components/common/ProgressionRule";
import Status from "components/presentation/Status";
import CaptionTable from "components/general/table/CaptionTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAsterisk } from "@fortawesome/free-solid-svg-icons";
import { isLiveStatus } from "helpers/status";
import SportCode from "components/common/SportCode";
import { useUrlFriendlyToParameters } from "helpers/urlFriendly";


function CslHeats() {

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const { mirs_competition, mirs_rsc, mirs_language } = useUrlFriendlyToParameters()
  const { discipline, sex, event, phase } = getRscObject(mirs_rsc)

  const cumHeat = `${discipline}${sex}${event}${phase}--------`
  const heats = [`${discipline}${sex}${event}${phase}000100--`, `${discipline}${sex}${event}${phase}000200--`]


  // Cumulative data
  const { data: cumulativeData, isError: isErrorCum, isLoading: isLoadingCum } = useQuery({
    queryKey: ['heatsCumulative', mirs_competition, mirs_language, cumHeat],
    queryFn: getCumulativeResult,
    refetchInterval: 5000,
    staleTime: 5000,
    refetchIntervalInBackground: true,
    notifyOnChangeProps: ['data', 'error'],
    onError: (error) =>
      console.error(`Something went wrong: ${error.message}`),
  });

  // Heat units
  const queryHeats = [];
  heats.forEach(heat => {
    queryHeats.push({
      queryKey: ['heatsUnit', mirs_competition, heat, mirs_language, cumHeat],
      queryFn: getResultsRSC,
      refetchInterval: 5000,
      staleTime: 5000,
      refetchIntervalInBackground: true,
      notifyOnChangeProps: ['data', 'error'],
    })
  });

  const heatsData = useQueries(queryHeats);

  const isLoading = isLoadingCum || heatsData.some(heat => heat.isLoading)
  const isError = isErrorCum || heatsData.some(heat => heat.isError);

  if (isError) { return <NotAvailable />; }
  if (isLoading) { return <Loader />; }

  let heat1Data = {};
  let heat2Data = {};
  let thisHeat = mirs_rsc.includes("HEAT000100") ? "1" : "2"
  let thisHeatHaveData = false;

  heatsData.forEach(heat => {
    if (heat?.data?.results?.eventUnitCode?.includes("HEAT000100")) {
      heat1Data = heat.data;
    }
    if (heat?.data?.results?.eventUnitCode?.includes("HEAT000200")) {
      heat2Data = heat.data;
    }
    if (heat?.data?.results?.eventUnitCode === mirs_rsc) {
      thisHeatHaveData = true;
    }
  })

  if (!thisHeatHaveData) { return <NotAvailable /> }

  return (
    <div className="csl-main">
      <ResultHeats cumulativeData={cumulativeData} heat1Data={heat1Data} heat2Data={heat2Data} heat={thisHeat} />
    </div>
  );
}

export default CslHeats;

const ResultHeats = ({ cumulativeData, heat1Data, heat2Data, heat }) => {

  const { breakPoint } = useViewport();
  const { mirs_rsc } = useUrlFriendlyToParameters()

  const isMobile = breakPoint === "sm" || breakPoint === "xs";

  const isHeat1 = heat === "1";

  const heatData = isHeat1 ? heat1Data : heat2Data;

  const data = heatData?.results?.items

  const resultStatus = heatData?.results?.status?.code;
  const scheduleStatus = heatData?.results?.schedule?.status?.code;

  const isStartList = heatData?.results?.status?.code === 'START_LIST'
  const isOfficial1 = heat1Data?.results?.status?.code === 'OFFICIAL'
  const isStartList1 = heat1Data?.results?.status?.code === 'START_LIST'
  const isOfficial2 = heat2Data?.results?.status?.code === 'OFFICIAL'
  const itCanBeOfficial2 = heat2Data?.results?.status?.code?.includes('OFFICIAL')

  const currentsInHeat1 = heat1Data?.results?.currentExtendedInfos?.filter(cei => cei?.ei_code === 'CURRENT')?.map(current => current?.ei_value);
  const currentsInHeat2 = heat2Data?.results?.currentExtendedInfos?.filter(cei => cei?.ei_code === 'CURRENT')?.map(current => current?.ei_value);

  const renderRowSubComponent = React.useCallback(({ row }) => {
    const componentExpanded = <TableExpanded row={row?.original} config={heat1Data?.config?.extendedConfig} heat1Data={heat1Data} heat2Data={heat2Data} heat={heat} />
    return (
      <LoaderSubComponent component={componentExpanded} />
    )
    // eslint-disable-next-line
  }, [resultStatus]);


  const hiddenColumns = () => {
    const startOrderHeat2Hide = itCanBeOfficial2 ? ['StartOrder'] : []
    if (isMobile) {
      return isHeat1 && isStartList1 ? ['Rank', 'QualMark', 'Best'] : isHeat1 ? ['Best'] : ['Best', ...startOrderHeat2Hide];
    }
    return isHeat1 && isStartList1 ? ['Rank', 'QualMark'] : isHeat1 ? [] : [...startOrderHeat2Hide];
  }

  const initialSortBy = () => {
    return !isStartList ? [{ id: 'Rank', desc: false }] : isHeat1 ? [{ id: 'StartOrder', desc: false }] : [{ id: 'Rank', desc: false }]
  }

  const tableColumns = useMemo(() => {
    return (
      [
        ColumnRank({ disableSort: isMobile }),
        ColumnStartOrder({ isHeat1: isHeat1, items: heatData?.results?.items, disableSort: isMobile }),
        ColumnBib({ disableSort: isMobile }),
        ColumnNoc({ disableSort: isMobile }),
        ColumnName({ disableSort: isMobile }),
        ColumnHeat1({
          isHeat1: isHeat1,
          items: heat1Data?.results?.items,
          label: "HeaderTime",
          disableSort: isMobile,
          hidePty: isMobile
        }),
        ColumnQualification({ className: 'text-center fw-bold' }),
        ColumnExpander({})

      ]
    )
  })
  const _captionField = (<CaptionTable rightCol={<Status typeToPrint="result" scheduleStatus={scheduleStatus} resultStatus={resultStatus} />} />)

  const currentsInThisHeat = isHeat1 ? currentsInHeat1 : currentsInHeat2;
  return <>
    <PageHeader data={heatData} />

    <div className='mirs-page-container'>

      {isLiveStatus(resultStatus) && <Currents data={heatData} cumulativeData={cumulativeData} currents={currentsInThisHeat} />}
      <ProgressionRule key="progress-component" data={heatData?.config} filter={{ type: ['QUALIFICATION'], code: ['QUAL_RULE'] }} />
      <MirsTable
        id={`mirs-table-result-${mirs_rsc}`}
        caption={_captionField}
        data={data}
        columns={tableColumns}
        keyRow={"participant-code"}
        initialSortBy={initialSortBy()}
        hiddenColumns={hiddenColumns()}
        renderRowSubComponent={renderRowSubComponent}
        getRowProps={row => {
          const isCurrent = currentsInThisHeat?.includes(row?.original?.participant?.code)
          return {
            className: isCurrent ? 'currentIndicator' : '',
          }
        }}

      />
    </div>
  </>

}

function ColumnStartOrder({
  disableSort = false,
  className = 'text-end',
  headerClassName = 'text-end w-5',
  label = 'LabelStartOrderN',
  items = [],
  isHeat1 = false
}) {
  return (
    {
      Header: <Label data={label} />,
      accessor: props => props?.startSortOrder,
      className: className,
      headerClassName: headerClassName,
      disableSortBy: disableSort,
      id: 'StartOrder',
      Cell: props => props.cell.row.original?.startOrder
    }
  )
}

function ColumnHeat1({
  disableSort = false,
  className = 'text-end',
  headerClassName = 'text-end w-10',
  label = 'HeaderRUN_1',
  items = [],
  isHeat1 = false,
  asterisc = (<FontAwesomeIcon className="font-icon" icon={faAsterisk} />),
  hidePty = false
}) {
  return (
    {
      Header: <Label data={label} />,
      className: className,
      accessor: props => props?.resultData ? props?.resultData : 1000 + props?.sortOrder,
      headerClassName: headerClassName,
      disableSortBy: disableSort,
      id: 'Run1',
      Cell: props => {
        const row  = props.cell.row.original;
        const out = row?.resultIrm
          ? <SportCode discipline="CSL" entity='@IRM' code={row?.resultIrm} abbr={true} />
          : row?.resultRank && !hidePty && row?.resultData
          ? `${row.resultData} (${row.resultRank})`
          : row?.resultData;
        const revision = row?.unchecked ? asterisc : '';
        return <>{revision}{out}</>
      }
    }
  )
}

function ColumnHeat2({
  disableSort = false,
  className = 'text-end',
  headerClassName = 'text-end w-10',
  label = 'HeaderRUN_2',
  items = [],
  isHeat1 = false,
  asterisc = (<FontAwesomeIcon className="font-icon" icon={faAsterisk} />),
  hidePty = false

}) {
  return (
    {
      Header: <Label data={label} />,
      className: className,
      headerClassName: headerClassName,
      accessor: (props) => {
        let res = items?.find(item => item?.participant?.code === props?.participant?.code);
        return res?.resultData ? res?.resultData : 1000 + res?.sortOrder;

      },
      disableSortBy: disableSort,
      id: 'Run2',
      Cell: (props) => {
        if (isHeat1) return '';
        let res = items?.find(item => item?.participant?.code === props.cell.row.original?.participant?.code);
        let out = (res?.resultIrm ?
          <SportCode discipline="CSL" entity='@IRM' code={res?.resultIrm} abbr={true} />
          : res?.resultRank ?
            <><span className={isBest(props, "HEAT000200") ? 'fw-bold' : ''}>{res.resultData}</span>{hidePty ? '' : ` (${res?.resultRank})`}</>
            : <span className={isBest(props, "HEAT000200") ? 'fw-bold' : ''}>{res.resultData}</span>
        )
        const revision = res?.unchecked ? asterisc : '';
        return <>{revision}{out}</>

      }
    }
  )
}

function ColumnBest({
  className = 'text-end',
  headerClassName = 'text-end w-10',
  label = 'HeaderBestTimeN',
  isHeat1 = false,
  disableSort = false
}) {
  return (
    {
      Header: <Label data={label} />,
      className: className,
      accessor: (props) => {
        return props?.resultData ? props?.resultData : 1000 + props?.sortOrder;
      },
      headerClassName: headerClassName,
      disableSortBy: disableSort,
      id: 'Best',
      Cell: (props) => {
        return props.cell.row.original?.resultData;
      }
    }
  )
}

const isBest = (props, heat) => {
  let itemBest = {};
  props.cell.row.original?.items?.forEach(item => {
    item.extendedResults?.forEach(ext => {
      if (ext?.er_value2 === 'Y') itemBest = item
    })
  });
  return itemBest?.eventUnit?.code?.includes(heat)
}
